import { useEffect, useState } from 'react'

import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

// Number input with a trailing € suffix.
//
// Deliberately `type="text"` + `inputMode="decimal"`: with `type="number"`
// browsers report an empty `e.target.value` for intermediate states such as
// "0." or "-", which wiped the field mid-typing and made decimals impossible.
// The raw text is kept in local state so the caret survives editing, and only
// parsed values are pushed to the form. Both "12.5" and "12,5" are accepted.
//
// Clearing the field emits `null` (not `undefined`) - `null` means "clear this
// on the server", while `undefined` would be dropped from the payload and the
// old value would come back on the next refetch.
export function MoneyInput({
  value,
  onChange,
  onBlur,
  className
}: {
  value?: number | null
  onChange: (value: number | null) => void
  onBlur?: () => void
  className?: string
}) {
  const [text, setText] = useState(value == null ? '' : String(value))
  const [focused, setFocused] = useState(false)

  // Follow external updates (form reset, fetched detail) unless the user is typing.
  useEffect(() => {
    if (!focused) setText(value == null ? '' : String(value))
  }, [value, focused])

  return (
    <div className="relative">
      <Input
        type="text"
        inputMode="decimal"
        className={cn('pr-9', className)}
        value={text}
        onFocus={() => setFocused(true)}
        onChange={(e) => {
          const raw = e.target.value
          if (raw !== '' && !/^-?\d*[.,]?\d*$/.test(raw)) return
          setText(raw)

          if (raw.trim() === '') {
            onChange(null)
            return
          }
          const parsed = Number(raw.replace(',', '.'))
          if (!Number.isNaN(parsed)) onChange(parsed)
        }}
        onBlur={() => {
          setFocused(false)
          setText(value == null ? '' : String(value))
          onBlur?.()
        }}
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
        €
      </span>
    </div>
  )
}
